import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mydawa.granularity.v1";
const DEFAULTS = { granularity: "monthly", from: "", to: "", compare: "none" };  // compare ∈ none|prev|yoy

const Ctx = createContext(null);

export function GranularityProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch (e) {
      console.warn("[granularity] read failed:", e);
    }
    return DEFAULTS;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
    catch (e) { console.warn("[granularity] write failed:", e); }
  }, [state]);

  const value = useMemo(() => ({
    ...state,
    set: (patch) => setState((s) => ({ ...s, ...patch })),
    reset: () => setState(DEFAULTS),
    query: () => {
      const p = new URLSearchParams();
      p.set("granularity", state.granularity);
      if (state.from) p.set("from_date", state.from);
      if (state.to) p.set("to_date", state.to);
      return p.toString();
    },
    // Compute the comparison window (previous period or year-over-year).
    // Returns null when compare === "none". Always yields ISO YYYY-MM-DD strings.
    compareQuery: () => {
      if (state.compare === "none") return null;
      // Figure out current range (explicit dates override, else default window)
      const shiftDays = { daily: 1, weekly: 7, monthly: 30,
                           quarterly: 91, annually: 365 }[state.granularity] || 30;
      const defaultSpan = shiftDays * 14;
      const toDate = state.to ? new Date(state.to) : new Date();
      const fromDate = state.from ? new Date(state.from) : new Date(toDate.getTime() - defaultSpan * 86400000);
      const span = Math.max(1, Math.round((toDate - fromDate) / 86400000));
      const backDays = state.compare === "yoy" ? 365 : span;
      const prevTo = new Date(toDate.getTime() - backDays * 86400000);
      const prevFrom = new Date(fromDate.getTime() - backDays * 86400000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const p = new URLSearchParams();
      p.set("granularity", state.granularity);
      p.set("from_date", iso(prevFrom));
      p.set("to_date", iso(prevTo));
      return p.toString();
    },
  }), [state]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGranularity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useGranularity must be used inside GranularityProvider");
  return ctx;
}

export function GranularityBar() {
  const g = useGranularity();
  const options = [
    { key: "daily", label: "Day" },
    { key: "weekly", label: "Week" },
    { key: "monthly", label: "Month" },
    { key: "quarterly", label: "Quarter" },
    { key: "annually", label: "Year" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="granularity-bar">
      <div className="inline-flex items-center rounded-full bg-[#fafcfa] p-1 text-xs font-semibold">
        {options.map((o) => (
          <button
            key={o.key}
            onClick={() => g.set({ granularity: o.key })}
            className={`rounded-full px-3 py-1 transition ${g.granularity === o.key ? "bg-[#66cc33] text-white shadow" : "text-[#5c665a] hover:text-[#1a1c19]"}`}
            data-testid={`gran-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1 text-[11px] text-[#5c665a]">
        <span>From</span>
        <input type="date" value={g.from} onChange={(e) => g.set({ from: e.target.value })}
               className="admin-input py-1 text-[11px]" data-testid="gran-from" />
        <span>to</span>
        <input type="date" value={g.to} onChange={(e) => g.set({ to: e.target.value })}
               className="admin-input py-1 text-[11px]" data-testid="gran-to" />
        {(g.from || g.to) && (
          <button onClick={() => g.set({ from: "", to: "" })} className="text-[11px] font-semibold text-[#c01974]">clear</button>
        )}
      </div>
      <div className="inline-flex items-center rounded-full border border-[#e2e8e2] bg-white p-1 text-xs font-semibold" data-testid="gran-compare">
        {[{k:"none",l:"No compare"},{k:"prev",l:"vs previous"},{k:"yoy",l:"vs year ago"}].map((o) => (
          <button key={o.k} onClick={() => g.set({ compare: o.k })}
                  className={`rounded-full px-2.5 py-0.5 transition ${g.compare === o.k ? "bg-[#ff3399] text-white shadow" : "text-[#5c665a] hover:text-[#1a1c19]"}`}
                  data-testid={`gran-compare-${o.k}`}>
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}
